// İSG uzmanı portalı — İş İzni Tipi formunun istenen belge seçicisi (api-contract §22, GL #7).
// Seçenekler §27 kaydından gelir (`DocumentTypeRef`, entity başına bir checkbox grubu, önce personel);
// tipte saklı ya da seçili kalan pasif türler "(pasif)" ile görünür kalır (DR 10). Seçim durumu ve ret
// eşlemesi `PermitTypeFormModal`'dadır; bu bileşen yalnız çizer ve kaydı açılışta tazeler.

import {
  Alert,
  Button,
  type DocumentTypeRef,
  REQUIREMENT_ENTITIES,
  Spinner,
  documentTypeRefKey,
  useDocumentTypes,
  useRefetchDocumentTypesOnMount,
} from '@isgportal/shared';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

const REQUIRED_DOCS_ERROR_ID = 'permit-type-required-docs-error';
const REQUIRED_DOCS_INACTIVE_ID = 'permit-type-required-docs-inactive';

/**
 * İstenen belge seçicisi (§22; `PermitTypeFormModal`). Her entity için kaydın AKTİF türleri,
 * ardından seçicide sunulmayan ama tipte saklı ya da seçili kalan türler ("(pasif)" — DR 10;
 * işaretliyse değişmeden gönderilir). Modal gövdesinde yalnız açıkken mount edilir: kayıt her
 * açılışta bir kez tazelenir (§27.7(2)).
 * Durumlar (Review Focus 2): yükleniyor → Spinner (komşu KKD/kontrol listeleriyle aynı); hata → yalnız
 * `isError && !types` iken Alert + "Tekrar dene"; bir entity'de hiç satır yoksa açıklama.
 */
export function RequiredDocumentsPicker({
  stored,
  selected,
  onToggle,
  rejected,
  containerRef,
}: {
  stored: readonly DocumentTypeRef[];
  selected: ReadonlyMap<string, DocumentTypeRef>;
  onToggle: (ref: DocumentTypeRef) => void;
  rejected: readonly DocumentTypeRef[];
  containerRef: RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  useRefetchDocumentTypesOnMount();
  const registry = useDocumentTypes();

  const rejectedError =
    rejected.length > 0 ? (
      <p id={REQUIRED_DOCS_ERROR_ID} role="alert" className="text-xs font-medium text-danger">
        {t('permitTypes.form.requiredDocs.rejected', {
          types: rejected.map((ref) => registry.label(ref.entity, ref.code)).join(', '),
        })}
      </p>
    ) : null;

  if (registry.types === undefined) {
    return (
      <>
        {registry.isError ? (
          <Alert tone="danger">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>{t('documentTypes.loadError')}</span>
              <Button variant="ghost" size="sm" onClick={() => void registry.refetch()}>
                {t('documentTypes.retry')}
              </Button>
            </div>
          </Alert>
        ) : (
          <Spinner label={t('documentTypes.loading')} />
        )}
        {rejectedError}
      </>
    );
  }

  const groups = REQUIREMENT_ENTITIES.map((entity) => {
    const active = registry.selectable(entity);
    const activeCodes = new Set(active.map((type) => type.code));
    // Seçicide olmayan ama saklı ya da seçili türler — tekilleştirilmiş, saklı sıra önce.
    const extra = new Map<string, DocumentTypeRef>();
    for (const ref of [...stored, ...selected.values()]) {
      if (ref.entity === entity && !activeCodes.has(ref.code)) {
        extra.set(documentTypeRefKey(ref), ref);
      }
    }
    const rows = [
      ...active.map((type) => ({ ref: { entity, code: type.code }, inactive: false })),
      ...[...extra.values()].map((ref) => ({ ref, inactive: true })),
    ];
    const hasCheckedInactive = [...extra.values()].some((ref) =>
      selected.has(documentTypeRefKey(ref)),
    );
    return { entity, rows, hasCheckedInactive };
  });
  const describedByFor = (group: (typeof groups)[number]) =>
    [
      group.hasCheckedInactive ? `${REQUIRED_DOCS_INACTIVE_ID}-${group.entity}` : null,
      rejectedError ? REQUIRED_DOCS_ERROR_ID : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div ref={containerRef} className="flex flex-col gap-4">
      {/* Checkbox grubu → native fieldset/legend (UserFormModal / KioskRegisterModal emsali). */}
      {groups.map((group) => {
        const { entity, rows, hasCheckedInactive } = group;
        return (
          <div key={entity} className="flex flex-col gap-2">
            <fieldset className="flex flex-col" aria-describedby={describedByFor(group)}>
              <legend className="mb-1.5 text-xs font-medium text-muted">
                {t(`permitTypes.form.requiredDocs.${entity}`)}
              </legend>
              {rows.length === 0 ? (
                <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted">
                  {t('documentTypes.empty')}
                </p>
              ) : (
                <div className="flex flex-col gap-1 rounded-md border border-border p-2">
                  {rows.map(({ ref, inactive }) => (
                    <label
                      key={documentTypeRefKey(ref)}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg hover:bg-surface-muted"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 accent-primary"
                        checked={selected.has(documentTypeRefKey(ref))}
                        onChange={() => onToggle(ref)}
                      />
                      <span className="min-w-0 break-words font-medium">
                        {registry.label(ref.entity, ref.code)}
                      </span>
                      {inactive ? (
                        <span className="shrink-0 text-xs text-muted">
                          {t('permitTypes.catalog.inactiveSuffix')}
                        </span>
                      ) : null}
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
            {hasCheckedInactive ? (
              <div id={`${REQUIRED_DOCS_INACTIVE_ID}-${entity}`}>
                <Alert tone="warning">{t('permitTypes.form.requiredDocs.inactiveHint')}</Alert>
              </div>
            ) : null}
          </div>
        );
      })}
      {rejectedError}
    </div>
  );
}
